import React, { useState, useEffect, useRef, useCallback, memo, useMemo } from 'react';
import { Bell, User, ExternalLink, Trash2, Loader2, Layers, Calendar, Grid, Rss, Heart, MessageCircle, Share2, Eye, MoreHorizontal, Bookmark, Send, Smile, Check } from 'lucide-react';
import { getMySubscriptions, unsubscribeFromCollection, getSubscribedFeed } from '../api';
import SubscribedUserCollectionsView from './SubscribedUserCollectionsView';
import cacheService from '../utils/cacheService';
import { toThumbnailUrl } from '../utils/imageThumbnailUrl';

// Optimized Lazy Image Component with IntersectionObserver
const LazyImage = memo(({ src, fallbackSrc, alt, className, aspectRatio = 'square' }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [error, setError] = useState(false);
  const [useFallback, setUseFallback] = useState(false);
  const imgRef = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    const img = imgRef.current;
    if (!img || isInView) return;

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observerRef.current?.disconnect();
        }
      },
      {
        rootMargin: '200px', // Preload images 200px before they come into view
        threshold: 0.01
      }
    );

    observerRef.current.observe(img);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [isInView]);

  // Optimize image URL for mobile devices
  const optimizedSrc = useMemo(() => {
    const activeSrc = useFallback ? fallbackSrc : src;
    if (!activeSrc) return null;
    
    // If using a CDN or image service, add optimization parameters
    // Example: add width/quality parameters for better mobile performance
    const isMobile = window.innerWidth < 768;
    const width = isMobile ? 400 : 600;
    
    // Check if URL supports query params for optimization
    if (activeSrc.includes('cloudinary') || activeSrc.includes('imgbb')) {
      return `${activeSrc}?w=${width}&q=80&f=auto`;
    }
    
    return activeSrc;
  }, [src, fallbackSrc, useFallback]);

  const aspectRatioClass = aspectRatio === 'square' ? 'aspect-square' : 'aspect-video';

  return (
    <div ref={imgRef} className={`relative ${aspectRatioClass} bg-gray-100 overflow-hidden`}>
      {/* Blur Placeholder */}
      {!isLoaded && (
        <div className="absolute inset-0 bg-gradient-to-br from-gray-200 via-gray-100 to-gray-200 animate-pulse" />
      )}
      
      {/* Actual Image */}
      {isInView && !error && (
        <img
          src={optimizedSrc}
          alt={alt}
          loading="lazy"
          decoding="async"
          className={`${className} transition-opacity duration-500 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => setIsLoaded(true)}
          onError={() => {
            if (!useFallback && fallbackSrc && fallbackSrc !== src) {
              setUseFallback(true);
              setError(false);
              setIsLoaded(false);
              return;
            }
            setError(true);
            setIsLoaded(true);
          }}
          // Use srcset for responsive images if possible
          srcSet={optimizedSrc ? `${optimizedSrc} 1x, ${optimizedSrc} 2x` : undefined}
        />
      )}
      
      {/* Error State */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <div className="text-center text-gray-400">
            <Layers size={32} className="mx-auto mb-2" />
            <p className="text-xs">Image unavailable</p>
          </div>
        </div>
      )}
    </div>
  );
});

LazyImage.displayName = 'LazyImage';

// Instagram-Style Feed Card Component
const FeedCard = memo(({ kit, showNotification }) => {
  const [isLiked, setIsLiked] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [showFullCaption, setShowFullCaption] = useState(false);
  const [likesCount, setLikesCount] = useState(Math.floor(Math.random() * 500) + 50); // Mock likes
  
  const creatorName = kit.user?.publicProfile?.brandName || kit.user?.name || 'Unknown Creator';
  const creatorAvatar = kit.user?.publicProfile?.avatarUrl;
  const baseImageUrl = kit.generatedImageUrl || kit.thumbnail || kit.productDetails?.image;
  const imageUrl = (typeof baseImageUrl === 'string' && /^https?:\/\//i.test(baseImageUrl))
    ? toThumbnailUrl(baseImageUrl)
    : baseImageUrl;
  const kitName = kit.name || 'Untitled Creation';
  
  const timeAgo = useMemo(() => {
    const now = new Date();
    const created = new Date(kit.createdAt);
    const diffInSeconds = Math.floor((now - created) / 1000);
    
    if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 604800)}w ago`;
    
    return created.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }, [kit.createdAt]);

  const handleLike = useCallback(() => {
    setIsLiked(prev => !prev);
    setLikesCount(prev => isLiked ? prev - 1 : prev + 1);
  }, [isLiked]);

  const handleSave = useCallback(() => {
    setIsSaved(prev => !prev);
    showNotification?.(isSaved ? 'Removed from saved' : 'Saved to collection', 'success');
  }, [isSaved, showNotification]);

  const handleShare = useCallback(async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: kitName,
          text: `Check out this AI creation by ${creatorName}!`,
          url: window.location.href
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        showNotification?.('Link copied to clipboard!', 'success');
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  }, [kitName, creatorName, showNotification]);

  const caption = `${kitName}${kit.productDetails?.description ? ` - ${kit.productDetails.description}` : ''}`;
  const shouldTruncate = caption.length > 100;

  return (
    <article className="bg-white mb-4 sm:mb-6 border border-gray-200 sm:rounded-lg">
      {/* Header - Creator Info */}
      <div className="flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3">
        <div className="flex items-center gap-2.5 sm:gap-3 flex-1 min-w-0">
          {/* Avatar */}
          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-pink-500/20">
            {creatorAvatar ? (
              <LazyImage 
                src={creatorAvatar} 
                alt={creatorName} 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-pink-500 to-orange-500 flex items-center justify-center">
                <User size={16} className="text-white" />
              </div>
            )}
          </div>
          
          {/* Creator Name & Time */}
          <div className="flex-1 min-w-0">
            <p className="text-xs sm:text-sm font-semibold text-gray-900 truncate">
              {creatorName}
            </p>
            <p className="text-[10px] sm:text-xs text-gray-500">{timeAgo}</p>
          </div>
        </div>
        
        {/* More Options */}
        <button 
          className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
          aria-label="More options"
        >
          <MoreHorizontal size={18} className="text-gray-700" />
        </button>
      </div>

      {/* Image */}
      <div className="relative w-full">
        <LazyImage 
          src={imageUrl} 
          fallbackSrc={baseImageUrl}
          alt={kitName}
          className="w-full h-full object-cover"
          aspectRatio="square"
        />
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between px-2 sm:px-3 py-2 sm:py-2.5">
        <div className="flex items-center gap-3 sm:gap-4">
          {/* Like Button */}
          <button 
            onClick={handleLike}
            className="group transition-transform active:scale-90"
            aria-label={isLiked ? "Unlike" : "Like"}
          >
            <Heart 
              size={24}
              className={`sm:w-7 sm:h-7 transition-colors ${
                isLiked 
                  ? 'fill-red-500 text-red-500' 
                  : 'text-gray-900 group-hover:text-gray-600'
              }`}
              strokeWidth={isLiked ? 0 : 2}
            />
          </button>
          
          {/* Comment Button */}
          <button 
            className="group transition-transform active:scale-90"
            aria-label="Comment"
          >
            <MessageCircle 
              size={24}
              className="sm:w-7 sm:h-7 text-gray-900 group-hover:text-gray-600 transition-colors"
              strokeWidth={2}
            />
          </button>
          
          {/* Share Button */}
          <button 
            onClick={handleShare}
            className="group transition-transform active:scale-90"
            aria-label="Share"
          >
            <Send 
              size={24}
              className="sm:w-7 sm:h-7 text-gray-900 group-hover:text-gray-600 transition-colors"
              strokeWidth={2}
            />
          </button>
        </div>
        
        {/* Save Button */}
        <button 
          onClick={handleSave}
          className="group transition-transform active:scale-90"
          aria-label={isSaved ? "Unsave" : "Save"}
        >
          <Bookmark 
            size={24}
            className={`sm:w-7 sm:h-7 transition-colors ${
              isSaved 
                ? 'fill-gray-900 text-gray-900' 
                : 'text-gray-900 group-hover:text-gray-600'
            }`}
            strokeWidth={2}
          />
        </button>
      </div>

      {/* Likes Count */}
      {likesCount > 0 && (
        <div className="px-3 sm:px-4 pb-1">
          <p className="text-xs sm:text-sm font-semibold text-gray-900">
            {likesCount.toLocaleString()} {likesCount === 1 ? 'like' : 'likes'}
          </p>
        </div>
      )}

      {/* Caption */}
      <div className="px-3 sm:px-4 pb-2 sm:pb-2.5">
        <p className="text-xs sm:text-sm">
          <span className="font-semibold text-gray-900 mr-2">{creatorName}</span>
          <span className="text-gray-900">
            {shouldTruncate && !showFullCaption 
              ? `${caption.substring(0, 100)}...` 
              : caption
            }
            {shouldTruncate && (
              <button
                onClick={() => setShowFullCaption(prev => !prev)}
                className="text-gray-500 ml-1 font-medium hover:text-gray-700"
              >
                {showFullCaption ? 'less' : 'more'}
              </button>
            )}
          </span>
        </p>
      </div>

      {/* View Comments */}
      <div className="px-3 sm:px-4 pb-2">
        <button className="text-xs sm:text-sm text-gray-500 hover:text-gray-700">
          View all comments
        </button>
      </div>

      {/* Add Comment */}
      <div className="px-3 sm:px-4 py-2 sm:py-2.5 border-t border-gray-100">
        <div className="flex items-center gap-2 sm:gap-3">
          <Smile size={20} className="text-gray-400" />
          <input
            type="text"
            placeholder="Add a comment..."
            className="flex-1 text-xs sm:text-sm bg-transparent outline-none placeholder-gray-400"
          />
          <button className="text-xs sm:text-sm font-semibold text-blue-500 hover:text-blue-700">
            Post
          </button>
        </div>
      </div>
    </article>
  );
}, (prevProps, nextProps) => {
  return prevProps.kit._id === nextProps.kit._id;
});

FeedCard.displayName = 'FeedCard';

// Consolidated feed card: groups kits created from the same fabric
const ConsolidatedFeedCard = memo(({ group }) => {
  const latestKit = group.latestKit;
  const creatorName = latestKit?.user?.publicProfile?.brandName || latestKit?.user?.name || 'Unknown Creator';
  const creatorAvatar = latestKit?.user?.publicProfile?.avatarUrl;
  const fabricName = latestKit?.productDetails?.name || latestKit?.name || 'Fabric';

  const fabricBaseImage =
    latestKit?.productDetails?.image ||
    latestKit?.productDetails?.originalImage ||
    latestKit?.thumbnail ||
    latestKit?.generatedImageUrl;

  const fabricImage = (typeof fabricBaseImage === 'string' && /^https?:\/\//i.test(fabricBaseImage))
    ? toThumbnailUrl(fabricBaseImage)
    : fabricBaseImage;

  const sortedKits = useMemo(() => (
    [...group.kits].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
  ), [group.kits]);

  const displayedKits = sortedKits.slice(0, 8);
  const remainingCount = Math.max(0, sortedKits.length - displayedKits.length);

  return (
    <article className="bg-white mb-4 sm:mb-6 border border-gray-200 sm:rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3">
        <div className="flex items-center gap-2.5 sm:gap-3 flex-1 min-w-0">
          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-pink-500/20">
            {creatorAvatar ? (
              <LazyImage src={creatorAvatar} alt={creatorName} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-pink-500 to-orange-500 flex items-center justify-center">
                <User size={16} className="text-white" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs sm:text-sm font-semibold text-gray-900 truncate">{creatorName}</p>
            <p className="text-[10px] sm:text-xs text-gray-500 truncate">
              {fabricName} • {group.kits.length} creation{group.kits.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </div>

      <div className="relative w-full">
        <LazyImage
          src={fabricImage}
          fallbackSrc={fabricBaseImage}
          alt={fabricName}
          className="w-full h-full object-cover"
          aspectRatio="square"
        />
      </div>

      <div className="px-3 sm:px-4 py-3">
        <p className="text-xs sm:text-sm font-semibold text-gray-900 mb-2">Related kits from same fabric</p>
        <div className="flex items-center gap-2 overflow-x-auto">
          {displayedKits.map((kit) => {
            const baseKitImage = kit.generatedImageUrl || kit.thumbnail || kit.productDetails?.image;
            const kitThumb = (typeof baseKitImage === 'string' && /^https?:\/\//i.test(baseKitImage))
              ? toThumbnailUrl(baseKitImage)
              : baseKitImage;
            return (
              <div key={kit._id} className="w-16 h-16 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0 bg-gray-50">
                {kitThumb ? (
                  <LazyImage
                    src={kitThumb}
                    fallbackSrc={baseKitImage}
                    alt={kit.name || 'Kit'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <Layers size={14} />
                  </div>
                )}
              </div>
            );
          })}
          {remainingCount > 0 && (
            <div className="w-16 h-16 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center text-xs font-semibold text-gray-600 flex-shrink-0">
              +{remainingCount}
            </div>
          )}
        </div>
      </div>
    </article>
  );
}, (prevProps, nextProps) => prevProps.group.id === nextProps.group.id && prevProps.group.kits.length === nextProps.group.kits.length);

ConsolidatedFeedCard.displayName = 'ConsolidatedFeedCard';

const MySubscriptionsView = ({ setActiveView, showNotification }) => {
  const [activeTab, setActiveTab] = useState('feed');
  const [subscriptions, setSubscriptions] = useState([]);
  const [feedItems, setFeedItems] = useState([]);
  const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState(true);
  const [isLoadingFeed, setIsLoadingFeed] = useState(true);
  const [unsubscribingId, setUnsubscribingId] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);
  const [feedPage, setFeedPage] = useState(1);
  const [feedPagination, setFeedPagination] = useState(null);
  
  // Ensure feedItems and subscriptions are always arrays
  const safeFeedItems = Array.isArray(feedItems) ? feedItems : [];
  const safeSubscriptions = Array.isArray(subscriptions) ? subscriptions : [];

  const consolidatedFeedGroups = useMemo(() => {
    const groupsMap = new Map();

    safeFeedItems.forEach((kit) => {
      const groupKey =
        kit?.productDetails?._id ||
        kit?.productDetails?.productId ||
        kit?.productDetails?.fabricId ||
        kit?.productDetails?.image ||
        kit?.productDetails?.originalImage ||
        kit?.sourceImage ||
        kit?._id;

      if (!groupKey) return;

      if (!groupsMap.has(groupKey)) {
        groupsMap.set(groupKey, { id: String(groupKey), kits: [], latestKit: kit });
      }

      const group = groupsMap.get(groupKey);
      group.kits.push(kit);

      const latestDate = new Date(group.latestKit?.createdAt || 0).getTime();
      const currentDate = new Date(kit?.createdAt || 0).getTime();
      if (currentDate > latestDate) {
        group.latestKit = kit;
      }
    });

    return Array.from(groupsMap.values()).sort((a, b) => {
      const aDate = new Date(a.latestKit?.createdAt || 0).getTime();
      const bDate = new Date(b.latestKit?.createdAt || 0).getTime();
      return bDate - aDate;
    });
  }, [safeFeedItems]);
  
  // Refs for infinite scroll
  const observerTarget = useRef(null);
  const prefetchedPage = useRef(null);
  const isLoadingMore = useRef(false);

  // Cache TTL - 2 minutes for feed, 5 minutes for subscriptions
  const FEED_CACHE_TTL = 2 * 60 * 1000;
  const SUBSCRIPTIONS_CACHE_TTL = 5 * 60 * 1000;

  useEffect(() => {
    loadSubscriptions();
  }, []);

  useEffect(() => {
    if (activeTab === 'feed') {
      loadFeed();
    }
  }, [activeTab, feedPage]);

  // Infinite scroll implementation
  useEffect(() => {
    if (!observerTarget.current || !feedPagination?.hasNextPage || isLoadingFeed) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isLoadingMore.current) {
          handleLoadMoreFeed();
        }
      },
      {
        rootMargin: '300px', // Trigger 300px before reaching the end
        threshold: 0.1
      }
    );

    observer.observe(observerTarget.current);

    return () => {
      observer.disconnect();
    };
  }, [feedPagination, isLoadingFeed]);

  // Prefetch next page when user scrolls
  useEffect(() => {
    if (feedPagination?.hasNextPage && !prefetchedPage.current && !isLoadingMore.current) {
      // Prefetch next page in the background
      const timer = setTimeout(() => {
        prefetchNextPage();
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [feedPagination]);

  const prefetchNextPage = async () => {
    if (prefetchedPage.current || !feedPagination?.hasNextPage) return;
    
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const nextPage = feedPage + 1;
      const cacheKey = `subscribed_feed_${nextPage}`;
      
      // Check cache first
      const cached = cacheService.get(cacheKey);
      if (cached && typeof cached === 'object') {
        prefetchedPage.current = cached;
        return;
      }

      const response = await getSubscribedFeed(token, nextPage, 20);
      const data = response?.data?.data;
      
      if (data && typeof data === 'object') {
        // Cache the prefetched data
        cacheService.set(cacheKey, data, FEED_CACHE_TTL);
        prefetchedPage.current = data;
      }
    } catch (error) {
      console.error('Error prefetching next page:', error);
      prefetchedPage.current = null;
    }
  };

  const loadSubscriptions = async () => {
    try {
      setIsLoadingSubscriptions(true);
      const token = localStorage.getItem('token');
      if (!token) {
        showNotification('Please login to view your subscriptions', 'error');
        setActiveView('login');
        return;
      }

      // Check cache first
      const cacheKey = 'my_subscriptions';
      const cached = cacheService.get(cacheKey);
      
      if (cached) {
        setSubscriptions(cached);
        setIsLoadingSubscriptions(false);
        
        // Load fresh data in background
        getMySubscriptions(token).then(response => {
          const freshData = response.data.data || [];
          setSubscriptions(freshData);
          cacheService.set(cacheKey, freshData, SUBSCRIPTIONS_CACHE_TTL);
        }).catch(console.error);
        
        return;
      }

      const response = await getMySubscriptions(token);
      const data = response.data.data || [];
      setSubscriptions(data);
      
      // Cache the data
      cacheService.set(cacheKey, data, SUBSCRIPTIONS_CACHE_TTL);
    } catch (error) {
      console.error('Error loading subscriptions:', error);
      if (error.response?.status === 401) {
        showNotification('Please login to view your subscriptions', 'error');
        setActiveView('login');
      } else {
        showNotification('Failed to load subscriptions. Please try again.', 'error');
      }
    } finally {
      setIsLoadingSubscriptions(false);
    }
  };

  const loadFeed = async () => {
    try {
      setIsLoadingFeed(true);
      const token = localStorage.getItem('token');
      if (!token) {
        showNotification('Please login to view your feed', 'error');
        setActiveView('login');
        return;
      }

      // Check if we have prefetched data
      if (prefetchedPage.current && feedPage > 1) {
        const prefetchedData = prefetchedPage.current;
        if (prefetchedData && typeof prefetchedData === 'object') {
          setFeedItems(prev => [...prev, ...(prefetchedData.kits || [])]);
          setFeedPagination(prefetchedData.pagination || null);
        }
        prefetchedPage.current = null;
        setIsLoadingFeed(false);
        return;
      }

      // Check cache first
      const cacheKey = `subscribed_feed_${feedPage}`;
      const cached = cacheService.get(cacheKey);
      
      if (cached && typeof cached === 'object') {
        if (feedPage === 1) {
          setFeedItems(Array.isArray(cached.kits) ? cached.kits : []);
        } else {
          setFeedItems(prev => [...prev, ...(Array.isArray(cached.kits) ? cached.kits : [])]);
        }
        setFeedPagination(cached.pagination || null);
        setIsLoadingFeed(false);
        
        // Load fresh data in background for page 1
        if (feedPage === 1) {
          getSubscribedFeed(token, feedPage, 20).then(response => {
            const freshData = response?.data?.data;
            if (freshData && typeof freshData === 'object') {
              setFeedItems(Array.isArray(freshData.kits) ? freshData.kits : []);
              setFeedPagination(freshData.pagination || null);
              cacheService.set(cacheKey, freshData, FEED_CACHE_TTL);
            }
          }).catch(console.error);
        }
        
        return;
      }

      const response = await getSubscribedFeed(token, feedPage, 20);
      const data = response?.data?.data;
      
      if (!data || typeof data !== 'object') {
        console.error('Invalid feed data structure:', data);
        setFeedItems([]);
        setFeedPagination(null);
        return;
      }
      
      if (feedPage === 1) {
        setFeedItems(Array.isArray(data.kits) ? data.kits : []);
      } else {
        setFeedItems(prev => [...prev, ...(Array.isArray(data.kits) ? data.kits : [])]);
      }
      
      setFeedPagination(data.pagination || null);
      
      // Cache the data
      cacheService.set(cacheKey, data, FEED_CACHE_TTL);
    } catch (error) {
      console.error('Error loading feed:', error);
      if (error.response?.status === 401) {
        showNotification('Please login to view your feed', 'error');
        setActiveView('login');
      } else {
        showNotification('Failed to load feed. Please try again.', 'error');
      }
      // Set empty arrays on error to prevent null reference errors
      setFeedItems([]);
      setFeedPagination(null);
    } finally {
      setIsLoadingFeed(false);
      isLoadingMore.current = false;
    }
  };

  const handleUnsubscribe = async (subscriptionId, userName) => {
    if (!window.confirm(`Are you sure you want to unsubscribe from ${userName}'s collections?`)) {
      return;
    }

    try {
      setUnsubscribingId(subscriptionId);
      const token = localStorage.getItem('token');
      await unsubscribeFromCollection(subscriptionId, token);
      
      // Remove from local state
      setSubscriptions(prev => prev.filter(sub => sub._id !== subscriptionId));
      showNotification(`Successfully unsubscribed from ${userName}'s collections`, 'success');
      
      // Clear feed cache and reload
      if (activeTab === 'feed') {
        // Clear all feed cache pages
        for (let i = 1; i <= feedPage; i++) {
          cacheService.delete(`subscribed_feed_${i}`);
        }
        setFeedPage(1);
        setFeedItems([]);
        loadFeed();
      }
      
      // Clear subscriptions cache
      cacheService.delete('my_subscriptions');
    } catch (error) {
      console.error('Error unsubscribing:', error);
      showNotification('Failed to unsubscribe. Please try again.', 'error');
    } finally {
      setUnsubscribingId(null);
    }
  };

  const handleViewCollection = useCallback((phoneNumber, userName) => {
    setViewingUser({ phoneNumber, userName });
  }, []);

  const handleBackToSubscriptions = useCallback(() => {
    setViewingUser(null);
  }, []);

  const handleLoadMoreFeed = useCallback(() => {
    if (feedPagination && feedPagination.hasNextPage && !isLoadingMore.current) {
      isLoadingMore.current = true;
      setFeedPage(prev => prev + 1);
    }
  }, [feedPagination]);

  // Show collections view if viewing a user
  if (viewingUser) {
    return (
      <SubscribedUserCollectionsView
        phoneNumber={viewingUser.phoneNumber}
        userName={viewingUser.userName}
        onBack={handleBackToSubscriptions}
        showNotification={showNotification}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-6 md:pb-6">
      <div className="max-w-2xl mx-auto">
        {/* Header - Instagram Style */}
        <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="px-3 sm:px-4 py-3 sm:py-4">
            <div className="flex items-center justify-between mb-3">
              <h1 className="text-lg sm:text-xl font-bold text-gray-900">Subscriptions</h1>
              <div className="flex items-center gap-2">
                <Bell size={24} className="text-gray-900" />
                {safeSubscriptions.length > 0 && (
                  <span className="text-xs text-gray-500">
                    {safeSubscriptions.length}
                  </span>
                )}
              </div>
            </div>
            
            {/* Tabs - Instagram Story Style */}
            <div className="flex gap-4 border-b border-gray-200 -mb-px">
              <button
                onClick={() => setActiveTab('feed')}
                className={`pb-3 px-1 font-semibold text-sm transition-all relative ${
                  activeTab === 'feed'
                    ? 'text-gray-900'
                    : 'text-gray-500'
                }`}
              >
                <Rss size={20} className="inline mb-0.5" />
                <span className="ml-2">Feed</span>
                {activeTab === 'feed' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900" />
                )}
              </button>
              <button
                onClick={() => setActiveTab('subscriptions')}
                className={`pb-3 px-1 font-semibold text-sm transition-all relative ${
                  activeTab === 'subscriptions'
                    ? 'text-gray-900'
                    : 'text-gray-500'
                }`}
              >
                <Grid size={20} className="inline mb-0.5" />
                <span className="ml-2">Following</span>
                {activeTab === 'subscriptions' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900" />
                )}
              </button>
            </div>
          </div>

          {/* Stories Section - Optional */}
          {activeTab === 'feed' && safeSubscriptions.length > 0 && (
            <div className="px-3 sm:px-4 py-3 overflow-x-auto scrollbar-hide">
              <div className="flex gap-3 sm:gap-4">
                {safeSubscriptions.slice(0, 10).map((subscription) => {
                  const user = subscription.user || subscription;
                  const userName = user.name || 'Unknown';
                  return (
                    <div key={subscription._id} className="flex flex-col items-center gap-1 flex-shrink-0">
                      <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full p-0.5 bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-500">
                        <div className="w-full h-full rounded-full bg-white p-0.5">
                          <div className="w-full h-full rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center overflow-hidden">
                            <User size={20} className="text-white" />
                          </div>
                        </div>
                      </div>
                      <span className="text-[10px] sm:text-xs text-gray-900 max-w-[60px] sm:max-w-[70px] truncate">
                        {userName.split(' ')[0]}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Tab Content */}
        {activeTab === 'feed' && (
          <div className="pt-2">
            {isLoadingFeed && feedPage === 1 ? (
              <div className="flex items-center justify-center py-16">
                <div className="text-center">
                  <Loader2 size={40} className="text-gray-400 animate-spin mx-auto mb-4" />
                  <p className="text-gray-500 text-sm sm:text-base">Loading your feed...</p>
                </div>
              </div>
            ) : safeFeedItems.length === 0 ? (
              <div className="bg-white rounded-lg sm:rounded-xl border border-gray-200 p-8 sm:p-12 text-center mx-3 sm:mx-0 mt-4">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border-2 border-gray-300 flex items-center justify-center mx-auto mb-4">
                  <Rss size={32} className="sm:w-10 sm:h-10 text-gray-400" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">No Posts Yet</h3>
                <p className="text-sm sm:text-base text-gray-500 mb-6 max-w-sm mx-auto">
                  When you follow creators, you'll see their AI creations here.
                </p>
                <button
                  onClick={() => setActiveTab('subscriptions')}
                  className="px-6 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-semibold text-sm"
                >
                  Find People to Follow
                </button>
              </div>
            ) : (
              <>
                {/* Feed - Instagram Style Single Column */}
                <div className="space-y-0">
                  {consolidatedFeedGroups.map((group) => (
                    <ConsolidatedFeedCard key={group.id} group={group} />
                  ))}
                </div>

                {/* Infinite Scroll Trigger */}
                {feedPagination && feedPagination.hasNextPage && (
                  <div
                    ref={observerTarget}
                    className="py-8 flex justify-center items-center"
                  >
                    {isLoadingFeed && feedPage > 1 ? (
                      <div className="flex items-center gap-2 text-gray-500">
                        <Loader2 size={20} className="animate-spin" />
                        <span className="text-sm">Loading more posts...</span>
                      </div>
                    ) : (
                      <div className="h-16 w-full" />
                    )}
                  </div>
                )}

                {/* End of Feed Message */}
                {!feedPagination?.hasNextPage && safeFeedItems.length > 0 && (
                  <div className="py-8 text-center">
                    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                      <Check size={24} className="text-gray-400" />
                    </div>
                    <p className="text-gray-500 text-sm font-semibold">You're all caught up</p>
                    <p className="text-gray-400 text-xs mt-1">You've seen all new posts</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'subscriptions' && (
          <div className="pt-4 px-3 sm:px-4">
            {isLoadingSubscriptions ? (
              <div className="flex items-center justify-center py-16">
                <div className="text-center">
                  <Loader2 size={40} className="text-gray-400 animate-spin mx-auto mb-4" />
                  <p className="text-gray-500 text-sm sm:text-base">Loading...</p>
                </div>
              </div>
            ) : safeSubscriptions.length === 0 ? (
              <div className="bg-white rounded-lg sm:rounded-xl border border-gray-200 p-8 sm:p-12 text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border-2 border-gray-300 flex items-center justify-center mx-auto mb-4">
                  <User size={32} className="sm:w-10 sm:h-10 text-gray-400" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">No Following Yet</h3>
                <p className="text-sm sm:text-base text-gray-500 mb-6 max-w-sm mx-auto">
                  Start following creators to see their collections and AI creations.
                </p>
                <button
                  onClick={() => setActiveView('home')}
                  className="px-6 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-semibold text-sm"
                >
                  Discover Creators
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {safeSubscriptions.map((subscription) => {
                  const user = subscription.user || subscription;
                  const phoneNumber = user.phone || subscription.phoneNumber;
                  const userName = user.name || 'Unknown User';
                  const collectionCount = subscription.collectionCount || 0;

                  return (
                    <div
                      key={subscription._id}
                      className="bg-white rounded-lg border border-gray-200 p-4"
                    >
                      <div className="flex items-center gap-3">
                        {/* Avatar */}
                        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center flex-shrink-0">
                          <User size={20} className="sm:w-6 sm:h-6 text-white" />
                        </div>
                        
                        {/* User Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm sm:text-base font-semibold text-gray-900 truncate">{userName}</h3>
                          <p className="text-xs sm:text-sm text-gray-500">
                            {collectionCount} {collectionCount === 1 ? 'collection' : 'collections'}
                          </p>
                          {subscription.subscribedAt && (
                            <p className="text-xs text-gray-400 mt-0.5">
                              Following since {new Date(subscription.subscribedAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                            </p>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col gap-2">
                          <button
                            onClick={() => handleViewCollection(phoneNumber, userName)}
                            className="px-4 py-1.5 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 transition-colors font-semibold text-xs sm:text-sm"
                          >
                            View
                          </button>
                          <button
                            onClick={() => handleUnsubscribe(subscription._id, userName)}
                            disabled={unsubscribingId === subscription._id}
                            className="px-4 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-xs sm:text-sm"
                          >
                            {unsubscribingId === subscription._id ? (
                              <Loader2 size={14} className="animate-spin mx-auto" />
                            ) : (
                              'Unfollow'
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MySubscriptionsView;
